"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { useIdentity } from "@/lib/api/session";
import { isOwnQuery, parseWalletDeepLink, walletTabFor, type WalletDeepLink as Link } from "@/lib/wallet/deep-link";
import { useShell } from "./ShellContext";

/* Opens the wallet from the address — `?modal=balance|deposit|withdraw`, with
   `depositMethod`, `depositToken` and `depositNetwork` for the deposit side —
   the whitelabel's QueryParamsProvider + WalletHubProvider.

   Signed out, the link opens the sign-in instead and is held: once a session
   exists the wallet opens on the tab that was asked for, as the whitelabel's
   pendingTabRef does after its register modal. The wallet itself keeps the
   address in step while it is open (WalletModal). */
export function WalletDeepLink() {
  const searchParams = useSearchParams();
  const { openWallet, openSignIn } = useShell();
  const { isSignedIn, isPending } = useIdentity();

  const query = searchParams.toString();
  const link = parseWalletDeepLink(new URLSearchParams(query));
  const ownRef = useRef(false);
  ownRef.current = isOwnQuery(query);
  const linkRef = useRef<Link>(link);
  linkRef.current = link;

  const held = useRef<Link | null>(null);

  const open = (target: Link) => {
    if (!target.modal) return;
    openWallet(walletTabFor(target.modal), { method: target.method, token: target.token, network: target.network });
  };
  const openRef = useRef(open);
  openRef.current = open;

  /* Acted on when the asked-for tab changes, not on every write the open
     wallet makes to the deposit parameters — and never on an address the
     wallet wrote itself. */
  useEffect(() => {
    const target = linkRef.current;

    if (!target.modal || isPending || ownRef.current) return;

    if (!isSignedIn) {
      held.current = target;
      openSignIn();
      return;
    }

    openRef.current(target);
  }, [link.modal, isPending]);

  useEffect(() => {
    if (!isSignedIn || !held.current) return;

    const target = held.current;
    held.current = null;
    openRef.current(target);
  }, [isSignedIn]);

  return null;
}
